/**
 * Feature Flags Service (Safe Zone / Feature Toggle)
 * Company-scoped flags. No row = disabled (safe default).
 * Used for Studio Production V2 and future optional features.
 */

import { supabase } from '@/lib/supabase';
import { UNIFIED_LEDGER_FLAG_KEYS } from '@/app/lib/unifiedLedgerFlagKeys';

export const FEATURE_KEYS = {
  STUDIO_PRODUCTION_V2: 'studio_production_v2',
  STUDIO_PRODUCTION_V3: 'studio_production_v3',
  STUDIO_CUSTOMER_INVOICE_V1: 'studio_customer_invoice_v1',
  /** Shadow unified ledger engine — default OFF; no row in DB = disabled */
  UNIFIED_LEDGER_ENGINE: UNIFIED_LEDGER_FLAG_KEYS.ENGINE,
  /** Pilot preview UI — default OFF; read only in Phase 2.1 */
  UNIFIED_LEDGER_PILOT: UNIFIED_LEDGER_FLAG_KEYS.PILOT,
  /** Ops kill switch — when ON, force legacy everywhere */
  UNIFIED_LEDGER_KILL_SWITCH: UNIFIED_LEDGER_FLAG_KEYS.KILL_SWITCH,
  /** Per-screen rollout gates (scaffold — default OFF) */
  UNIFIED_LEDGER_SCREEN_LEDGER_V2: UNIFIED_LEDGER_FLAG_KEYS.SCREEN_LEDGER_V2,
  UNIFIED_LEDGER_SCREEN_ACCOUNT_STATEMENT: UNIFIED_LEDGER_FLAG_KEYS.SCREEN_ACCOUNT_STATEMENT,
  UNIFIED_LEDGER_SCREEN_TRIAL_BALANCE: UNIFIED_LEDGER_FLAG_KEYS.SCREEN_TRIAL_BALANCE,
  UNIFIED_LEDGER_SCREEN_ROZNAMCHA: UNIFIED_LEDGER_FLAG_KEYS.SCREEN_ROZNAMCHA,
  UNIFIED_LEDGER_SCREEN_PARTY_LEDGER: UNIFIED_LEDGER_FLAG_KEYS.SCREEN_PARTY_LEDGER,
} as const;

export type FeatureKey = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS];

export interface FeatureFlagRow {
  id: string;
  company_id: string;
  feature_key: string;
  enabled: boolean;
  description?: string | null;
  created_at?: string;
  updated_at?: string;
}

export const featureFlagsService = {
  /**
   * Get all feature flags for a company.
   * Missing row for a key = treated as disabled.
   */
  async getAll(companyId: string): Promise<Record<string, boolean>> {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('feature_key, enabled')
      .eq('company_id', companyId);

    if (error) {
      if (error.code === '42P01') return {}; // table does not exist yet
      console.warn('[FEATURE_FLAGS] getAll error:', error);
      return {};
    }

    const map: Record<string, boolean> = {};
    for (const row of data || []) {
      map[row.feature_key] = row.enabled === true;
    }
    return map;
  },

  /**
   * Check if a feature is enabled for the company.
   * No row or enabled=false → false.
   */
  async isEnabled(companyId: string, featureKey: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('enabled')
      .eq('company_id', companyId)
      .eq('feature_key', featureKey)
      .maybeSingle();

    if (error || !data) return false;
    return data.enabled === true;
  },

  /**
   * Set a feature flag. Upserts by (company_id, feature_key).
   */
  async setEnabled(
    companyId: string,
    featureKey: string,
    enabled: boolean,
    description?: string
  ): Promise<void> {
    const { error } = await supabase
      .from('feature_flags')
      .upsert(
        {
          company_id: companyId,
          feature_key: featureKey,
          enabled,
          ...(description != null && { description }),
        },
        { onConflict: 'company_id,feature_key' }
      );

    if (error) throw error;
  },
};
